import { z } from "zod";
import { formConfigToZodSchemas, getFormConfigDefaultValues } from "../form-config-to-zod-schema";
import {
  getFormKeys,
  type FormConfig,
  type FormConfigInPages,
  type I18NFunction,
  type SectionConfig,
} from "../form-config-types";
import { mapToYesNoEnumLabels, yesNoEnum } from "./form-data-enums";
import { enumErrorMap } from "./form-data-utils";

const initialPresentationFormConfigInPages = (l: I18NFunction) =>
  [
    // page 1
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        pointOfInvolvement: {
          type: "textarea",
          label: l(
            "1. At what point in the patient journey do you typically become involved in the care of NMSC (CSCC/BCC) patients?",
            "1. À quel moment du parcours du patient êtes-vous généralement impliqué dans le traitement des patients atteints de cancer de la peau non-mélanome (NMSC) (Cutaneous Squamous-Cell Carcinoma; CSCC / Basal Cell Carcinoma; BCC)?",
          ),

          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, {
              message: l("Please enter a value", "Ce champ est requis"),
            }),
        },
        delaysInPresentationAndEvaluation: {
          type: "object",
          label: "",
          properties: {
            hasDelays: {
              type: "radioGroup",
              label: l(
                "2. Are there delays in patient presentation and evaluation?",
                "2. Notez-vous des retards dans la présentation et l'évaluation des patients?",
              ),
              schema: z.enum(yesNoEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToYesNoEnumLabels,
              cols: 2,
            },
            top3Causes: {
              type: "textarea",
              label: l(
                "Please list the top 3 causes for the delays.",
                "Veuillez énumérer les trois principales causes des retards.",
              ),
              // schema: z.string({
              //   required_error: l("Please enter a value", "Ce champ est requis"),
              // }).min(1, {
              //   message: l("Please enter a value", "Ce champ est requis")
              // }),
              // We do not show the error here, only in the
              schema: z.string().nullish(),
              // hideIf: ["delaysInPresentationAndEvaluation.hasDelays", "not-equals", "Yes"],
              // requireIf: ["delaysInPresentationAndEvaluation.hasDelays", "equals", "Yes"],
              dependencies: [
                {
                  if: ["delaysInPresentationAndEvaluation.hasDelays", "not-equals", "Yes"],
                  then: "hide",
                },
                {
                  if: ["delaysInPresentationAndEvaluation.hasDelays", "equals", "Yes"],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 1.3 For GP/PCP: How do you triage and determine what specialty you will refer your NMSC patients to? [Open box] [Not applicable]
        howDoYouTriageAndDetermineSpecialty: {
          type: "textarea",
          label: l(
            "3. For Primary Care Physician: How do you triage and determine what specialty you will refer your NMSC (CSCC/BCC) patients to?",
            "3. Pour les médecins généralistes: Comment triez-vous et déterminez-vous la spécialité vers laquelle vous orienterez vos patients atteints de NMSC (CSCC/BCC)?",
          ),
          // schema: z.string({
          //   required_error: l("Please enter a value", "Ce champ est requis"),
          // }).min(1, {
          //   message: l("Please enter a value", "Ce champ est requis")
          // }),
          schema: z
            .string()
            .min(1, {
              message: l("Please enter a value", "Ce champ est requis"),
            })
            .or(z.literal("not_applicable")),
          // offerNotApplicable: true,
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 1.5 Is the use of these guidelines consistent across the region? [Yes or No] [If selected No] Please describe the disparities [Open box]
        guidelinesAcrossRegions: {
          type: "object",
          label: l(
            "4. Which guidelines do you follow for triaging NMSC (CSCC/BCC) patients for further care following initial presentation?",
            "4. Quelles lignes directrices suivez-vous pour le triage des patients atteints de NMSC (CSCC/BCC) pour des soins supplémentaires après la présentation initiale?",
          ),
          offerNotApplicableForEntireObject: true,
          properties: {
            // 1.4 Which guidelines do you follow for triaging NMSC patients for further care following initial presentation? [Open box] [Not applicable; if selected, skip question 1.5]
            whichGuidelinesDoYouFollowForTriage: {
              type: "textarea",
              label: "",
              schema: z
                .string({ invalid_type_error: l("Please enter a value", "Ce champ est requis") })
                .min(1, {
                  message: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
              hideNotApplicableCheckbox: true,
            },
            isConsistent: {
              type: "radioGroup",
              label: l(
                "Is the use of these guidelines consistent across the region?",
                "L'utilisation de ces lignes directrices est-elle uniforme à travers la province?",
              ),
              //(l) =>
              schema: z
                .enum(yesNoEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToYesNoEnumLabels,
              hideNotApplicableCheckbox: true,
              cols: 2,
            },
            disparities: {
              type: "textarea",
              label: l("Please describe the disparities", "5.1. Veuillez décrire les disparités"),
              schema: z.string({ invalid_type_error: l("Please enter a value", "Ce champ est requis") }).nullish(),
              // hideIf: ["isTheUseOfTheseGuidelinesConsistentAcrossTheRegion.isConsistent", "not-equals", "No"],
              // requireIf: ["isTheUseOfTheseGuidelinesConsistentAcrossTheRegion.isConsistent", "equals", "No"],
              dependencies: [
                {
                  if: ["guidelinesAcrossRegions.isConsistent", "not-equals", "No"],
                  then: "hide",
                },
                {
                  if: ["guidelinesAcrossRegions.isConsistent", "equals", "No"],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        initialDoYouHaveAnyAdditionalComments: {
          type: "textarea",
          label: l(
            "Do you have any additional comments, suggestions, or concerns regarding the current version of the schema, particularly in relation to your own personal practice?",
            "Avez-vous d'autres commentaires, suggestions ou préoccupations au sujet de la version actuelle du schéma, particulièrement en ce qui concerne votre pratique personnelle?",
          ),
          schema: z.string({ invalid_type_error: l("Please enter a value", "Ce champ est requis") }).nullish(),
        },
      },
    },
  ] as const satisfies FormConfigInPages;

export const initialPresentationForm: (l: I18NFunction) => SectionConfig = (l) => ({
  sectionId: "initial-presentation",
  sectionNumber: 1,
  title: l("Initial Presentation & Evaluation", "Présentation initiale et évaluation"),
  description: l("Initial Presentation & Evaluation", "Présentation initiale et évaluation"),
  diagrams: [
    {
      src: l("/diagrams/initial_presentation.png", "/diagrams/initial_presentation_fr.png"),
      alt: l("Initial Presentation & Evaluation", "Présentation initiale et évaluation"),
    },
  ],
  formPages: initialPresentationFormConfigInPages(l).map((page) => ({
    formConfig: page.formFields,
    formSchemas: formConfigToZodSchemas(page.formFields, l),
    defaultValues: getFormConfigDefaultValues(page.formFields),
  })),
  responseKeyToFormConfig: Object.fromEntries(
    initialPresentationFormConfigInPages(l).flatMap((page) =>
      Object.entries(page.formFields).map(([key, field]) => [key, field]),
    ),
  ),
});
